/**
 * 实体表（虚拟化）：/v1/entities 的真实行 + 行内快捷操作。
 *
 * 与旧 `VirtualTable`（指标表，绑定 mock AdRow）分开实现：实体行没有指标字段，
 * 且需要「暂停 / 启动 / 改预算」三颗行内按钮。虚拟化策略与旧表一致：
 * 固定行高、absolute + translateY、React.memo 行组件、滚动中不 setState。
 *
 * 安全约定：行内按钮只**收集意图**，绝不直接写；真正执行必须先 dry-run →
 * 确认对话框 → `dryRun:false, confirm:true`（见 AdsPage / ActionPlanDialog）。
 */

import { memo, useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { AdEntityRow } from "../lib/apiTypes.js";
import {
  ENTITY_GRID_TEMPLATE,
  ENTITY_SELECT_WIDTH,
  ENTITY_TABLE_WIDTH,
  entityColumns,
  entityStatusText,
  supportsBudget,
  type EntityTableRow,
} from "../data/entityColumns.js";
import type { EntitySortDir } from "../data/entitySelectors.js";

export interface EntityTableProps {
  readonly rows: readonly EntityTableRow[];
  readonly rowHeight: number;
  readonly overscan: number;
  readonly selectedIds: ReadonlySet<string>;
  readonly focusedIndex: number;
  readonly sortId: string;
  readonly sortDir: EntitySortDir;
  readonly pageStep: number;
  readonly busy: boolean;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onSort: (columnId: string) => void;
  readonly onFocusIndex: (index: number) => void;
  readonly onToggleSelect: (index: number) => void;
  readonly onExtendSelection: (index: number) => void;
  readonly onSelectAll: () => void;
  readonly onClearSelection: () => void;
  readonly onPause: (row: AdEntityRow) => void;
  readonly onActivate: (row: AdEntityRow) => void;
  readonly onEditBudget: (row: AdEntityRow) => void;
  readonly onLoadMore: () => void;
  readonly onScrollElement: (element: HTMLDivElement | null) => void;
}

interface EntityRowProps {
  readonly row: EntityTableRow;
  readonly index: number;
  readonly offsetY: number;
  readonly height: number;
  readonly isSelected: boolean;
  readonly isFocused: boolean;
  readonly busy: boolean;
  readonly onToggleSelect: (index: number) => void;
  readonly onPause: (row: AdEntityRow) => void;
  readonly onActivate: (row: AdEntityRow) => void;
  readonly onEditBudget: (row: AdEntityRow) => void;
}

function StatusChip({ status }: { readonly status: string }): ReactElement {
  const upper = status.toUpperCase();
  const tone =
    upper === "ACTIVE"
      ? "border-success/40 bg-success-subtle text-success-text"
      : upper === "PAUSED" || upper === "ARCHIVED"
        ? "border-line bg-sunken text-ink-2"
        : "border-warning/40 bg-warning-subtle text-warning-text";
  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-2xs ${tone}`}>
      {entityStatusText(status)}
    </span>
  );
}

const EntityRow = memo(function EntityRow({
  row,
  index,
  offsetY,
  height,
  isSelected,
  isFocused,
  busy,
  onToggleSelect,
  onPause,
  onActivate,
  onEditBudget,
}: EntityRowProps) {
  const background = isSelected ? "bg-selected" : index % 2 === 1 ? "bg-sunken/40" : "bg-transparent";
  const isActive = row.status.toUpperCase() === "ACTIVE";

  return (
    <div
      role="row"
      data-row-index={index}
      aria-rowindex={index + 2}
      aria-selected={isSelected}
      className={`row-contain absolute left-0 top-0 grid items-center border-b border-line-subtle ${background}`}
      style={{
        gridTemplateColumns: ENTITY_GRID_TEMPLATE,
        width: ENTITY_TABLE_WIDTH,
        height,
        transform: `translateY(${offsetY}px)`,
        outline: isFocused ? "2px solid var(--ring-color)" : undefined,
        outlineOffset: isFocused ? "-2px" : undefined,
      }}
    >
      <div role="gridcell" className="flex items-center justify-center">
        <input
          type="checkbox"
          checked={isSelected}
          tabIndex={-1}
          aria-label={`选择 ${row.name}`}
          onChange={() => onToggleSelect(index)}
          className="h-3.5 w-3.5 accent-[var(--accent)]"
        />
      </div>

      {entityColumns.map((column) => (
        <div
          key={column.id}
          role="gridcell"
          title={column.hint}
          className={`truncate px-cell-x text-table text-ink ${
            column.align === "end" ? "tnum text-right" : "text-left"
          }`}
        >
          {column.id === "status" ? <StatusChip status={row.status} /> : column.text(row)}
        </div>
      ))}

      <div role="gridcell" className="flex items-center justify-end gap-1 px-cell-x">
        <button
          type="button"
          disabled={busy}
          onClick={() => (isActive ? onPause(row) : onActivate(row))}
          className={`rounded-control border px-1.5 py-0.5 text-2xs transition-colors dur-fast disabled:opacity-40 ${
            isActive
              ? "border-danger/40 bg-danger-subtle text-danger-text hover:opacity-90"
              : "border-line text-ink-2 hover:bg-hover"
          }`}
        >
          {isActive ? "暂停" : "启动"}
        </button>
        {supportsBudget(row.level) ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onEditBudget(row)}
            className="rounded-control border border-line px-1.5 py-0.5 text-2xs text-ink-2 transition-colors dur-fast hover:bg-hover disabled:opacity-40"
          >
            改预算
          </button>
        ) : null}
      </div>
    </div>
  );
});

function clamp(value: number, max: number): number {
  if (max < 0) return 0;
  return Math.min(Math.max(0, value), max);
}

export function EntityTable({
  rows,
  rowHeight,
  overscan,
  selectedIds,
  focusedIndex,
  sortId,
  sortDir,
  pageStep,
  busy,
  hasMore,
  loadingMore,
  onSort,
  onFocusIndex,
  onToggleSelect,
  onExtendSelection,
  onSelectAll,
  onClearSelection,
  onPause,
  onActivate,
  onEditBudget,
  onLoadMore,
  onScrollElement,
}: EntityTableProps): ReactElement {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => rowHeight,
    overscan,
    getItemKey: (index) => rows[index]?.id ?? index,
  });

  useEffect(() => {
    virtualizer.measure();
  }, [rowHeight, virtualizer]);

  useEffect(() => {
    onScrollElement(scrollElement);
  }, [scrollElement, onScrollElement]);

  useEffect(() => {
    if (focusedIndex < 0 || focusedIndex >= rows.length) return;
    virtualizer.scrollToIndex(focusedIndex, { align: "auto" });
  }, [focusedIndex, rows.length, virtualizer]);

  const registerViewport = useCallback((node: HTMLDivElement | null) => {
    viewportRef.current = node;
    setScrollElement(node);
  }, []);

  const moveFocus = useCallback(
    (next: number, extend: boolean) => {
      const target = clamp(next, rows.length - 1);
      onFocusIndex(target);
      virtualizer.scrollToIndex(target, { align: "auto" });
      if (extend) onExtendSelection(target);
    },
    [onExtendSelection, onFocusIndex, rows.length, virtualizer],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const meta = event.metaKey || event.ctrlKey;
      const element = viewportRef.current;

      if (meta && event.key.toLowerCase() === "a") {
        event.preventDefault();
        onSelectAll();
        return;
      }

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          moveFocus(focusedIndex + 1, event.shiftKey);
          return;
        case "ArrowUp":
          event.preventDefault();
          moveFocus(focusedIndex - 1, event.shiftKey);
          return;
        case "PageDown":
          event.preventDefault();
          moveFocus(focusedIndex + pageStep, event.shiftKey);
          return;
        case "PageUp":
          event.preventDefault();
          moveFocus(focusedIndex - pageStep, event.shiftKey);
          return;
        case "Home":
          event.preventDefault();
          moveFocus(0, event.shiftKey);
          return;
        case "End":
          event.preventDefault();
          moveFocus(rows.length - 1, event.shiftKey);
          return;
        case " ":
          event.preventDefault();
          onToggleSelect(clamp(focusedIndex, rows.length - 1));
          return;
        case "Escape":
          event.preventDefault();
          onClearSelection();
          return;
        case "ArrowLeft":
          event.preventDefault();
          element?.scrollBy({ left: -120, behavior: "auto" });
          return;
        case "ArrowRight":
          event.preventDefault();
          element?.scrollBy({ left: 120, behavior: "auto" });
          return;
        default:
          return;
      }
    },
    [focusedIndex, moveFocus, onClearSelection, onSelectAll, onToggleSelect, pageStep, rows.length],
  );

  const virtualItems = virtualizer.getVirtualItems();
  const renderedRows = virtualItems.length;
  const totalSize = virtualizer.getTotalSize();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        ref={registerViewport}
        tabIndex={0}
        role="grid"
        aria-label={`广告实体表，共 ${rows.length} 行`}
        aria-rowcount={rows.length + 1}
        aria-colcount={entityColumns.length + 2}
        onKeyDown={handleKeyDown}
        className="scroll-pane min-h-0 flex-1 overflow-auto"
      >
        <div style={{ width: ENTITY_TABLE_WIDTH }}>
          <div
            role="row"
            className="sticky top-0 z-[var(--z-sticky)] grid items-center border-b border-line bg-sunken"
            style={{ gridTemplateColumns: ENTITY_GRID_TEMPLATE, height: "var(--row-height-head)" }}
          >
            <div role="columnheader" className="flex items-center justify-center">
              <span className="sr-only">选择</span>
            </div>
            {entityColumns.map((column) => {
              const isSorted = column.id === sortId;
              return (
                <div
                  key={column.id}
                  role="columnheader"
                  aria-sort={isSorted ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  className="flex min-w-0 items-center px-cell-x"
                >
                  <button
                    type="button"
                    onClick={() => onSort(column.id)}
                    title={column.hint}
                    className={`flex w-full items-center gap-1 text-meta font-medium text-ink-3 transition-colors dur-fast hover:text-ink ${
                      column.align === "end" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <span className="truncate">{column.label}</span>
                    <span aria-hidden="true" className={isSorted ? "text-ink" : "opacity-0"}>
                      {isSorted && sortDir === "asc" ? "▲" : "▼"}
                    </span>
                  </button>
                </div>
              );
            })}
            <div role="columnheader" className="flex items-center justify-end px-cell-x text-meta font-medium text-ink-3">
              快捷操作
            </div>
          </div>

          <div style={{ height: totalSize, position: "relative" }}>
            {virtualItems.map((item) => {
              const row = rows[item.index];
              if (row === undefined) return null;
              return (
                <EntityRow
                  key={item.key}
                  row={row}
                  index={item.index}
                  offsetY={item.start}
                  height={item.size}
                  isSelected={selectedIds.has(row.id)}
                  isFocused={item.index === focusedIndex}
                  busy={busy}
                  onToggleSelect={onToggleSelect}
                  onPause={onPause}
                  onActivate={onActivate}
                  onEditBudget={onEditBudget}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line-subtle bg-surface px-3 py-1 text-2xs text-ink-3">
        <span className="tnum">
          已加载 {rows.length} 行 · 当前 DOM 渲染 {renderedRows} 行（虚拟化窗口 = 视口 + overscan {overscan}）· 选择列{" "}
          {ENTITY_SELECT_WIDTH}px
        </span>
        <span className="flex items-center gap-2">
          <span className="tnum">行高 {rowHeight}px · 总高 {Math.round(totalSize)}px</span>
          {hasMore ? (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="rounded-control border border-line px-2 py-0.5 text-2xs text-ink-2 transition-colors dur-fast hover:bg-hover disabled:opacity-40"
            >
              {loadingMore ? "加载中…" : "加载更多（游标）"}
            </button>
          ) : (
            <span>已到末页</span>
          )}
        </span>
      </div>
    </div>
  );
}
